import {useMutation} from "@apollo/client";
import {yupResolver} from "@hookform/resolvers/yup";
import classNames from "classnames";
import {useForm} from "react-hook-form";
import {toast} from "react-hot-toast";
import {useLocation, useNavigate} from "react-router";
import * as Yup from "yup";

import Button from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {RequiredFieldsNote} from "@/components/form/requiredFieldsNote/RequiredFieldsNote.tsx";
import {GQL_CUSTOM_QUOTE_REQUEST} from "@/lib/queries/billing.ts";

export interface CustomQuoteRequestFormValues {
  first_name: string;
  last_name: string;
  email: string;
  company_name: string;
  custom_monitoring_needs: string;
  additional_comments?: string;
}

export const CustomQuoteRequestFormSchema = Yup.object().shape({
  first_name: Yup.string().required("This field is required"),
  last_name: Yup.string().required("This field is required"),
  email: Yup.string().email("Please supply a valid email address").required("This field is required"),
  company_name: Yup.string().required("This field is required"),
  custom_monitoring_needs: Yup.string().required("This field is required"),
  additional_comments: Yup.string().optional(),
});

export const CustomQuoteRequestForm = () => {
  const {user} = useCurrentUserContext();
  const navigate = useNavigate();
  const location = useLocation();
  const customMonitoringNeeds = location.state?.custom_monitoring_needs || "";
  const {
    handleSubmit,
    formState: {errors},
    register,
  } = useForm<CustomQuoteRequestFormValues>({
    resolver: yupResolver<CustomQuoteRequestFormValues>(CustomQuoteRequestFormSchema),
    defaultValues: {
      first_name: user?.first_name,
      last_name: user?.last_name,
      company_name: user?.account.name,
      email: user?.email,
      custom_monitoring_needs: customMonitoringNeeds,
    },
  });
  const [submitCustomQuote] = useMutation(GQL_CUSTOM_QUOTE_REQUEST);
  return (
    <form
      onSubmit={handleSubmit(async (data) => {
        try {
          await submitCustomQuote({
            variables: {
              data,
            },
          });
          navigate("/billing/subscription/done?action=custom-request");
        } catch {
          toast.error("Something went wrong. Please try again later.");
        }
      })}
    >
      <RequiredFieldsNote />
      <div className="row">
        <div className="col-6">
          <FormLabel forId="first_name" required={true}>
            First Name
          </FormLabel>
          <input
            className={classNames("form-control", {"is-invalid": errors?.first_name})}
            type="text"
            {...register("first_name")}
          />
          {errors.first_name && <div className="invalid-feedback d-block">{errors.first_name?.message}</div>}
        </div>
        <div className="col-6">
          <FormLabel forId="last_name" required={true}>
            Last Name
          </FormLabel>
          <input
            className={classNames("form-control", {"is-invalid": errors?.last_name})}
            type="text"
            {...register("last_name")}
          />
          {errors.last_name && <div className="invalid-feedback d-block">{errors.last_name?.message}</div>}
        </div>
      </div>
      <div className="row mt-4">
        <div className="col-12">
          <FormLabel forId="email" required={true}>
            Company Email
          </FormLabel>
          <input
            className={classNames("form-control", {"is-invalid": errors?.email})}
            type="text"
            {...register("email")}
          />
          {errors.email && <div className="invalid-feedback d-block">{errors.email?.message}</div>}
        </div>
      </div>
      <div className="row mt-4">
        <div className="col-12">
          <FormLabel forId="company_name" required={true}>
            Company Name
          </FormLabel>
          <input
            className={classNames("form-control", {"is-invalid": errors?.company_name})}
            type="text"
            {...register("company_name")}
          />
          {errors.company_name && <div className="invalid-feedback d-block">{errors.company_name?.message}</div>}
        </div>
      </div>
      <div className="row mt-4">
        <div className="col-12">
          <FormLabel forId="custom_monitoring_needs" required={true}>
            Custom Monitoring Needs
          </FormLabel>
          <textarea
            className={classNames("form-control", {"is-invalid": errors?.custom_monitoring_needs})}
            rows={4}
            {...register("custom_monitoring_needs")}
          />
          {errors.custom_monitoring_needs && (
            <div className="invalid-feedback d-block">{errors.custom_monitoring_needs?.message}</div>
          )}
        </div>
      </div>
      <div className="row mt-4">
        <div className="col-12">
          <FormLabel forId="company_name">Additional Comments</FormLabel>
          <textarea
            className={classNames("form-control", {"is-invalid": errors?.additional_comments})}
            rows={4}
            {...register("additional_comments")}
          />
          {errors.additional_comments && (
            <div className="invalid-feedback d-block">{errors.additional_comments?.message}</div>
          )}
        </div>
      </div>
      <div className="row mt-4">
        <div className="col-12 d-flex justify-content-end gap-2">
          <Button variant={"secondary"} onClick={() => navigate(-1)}>
            Go Back
          </Button>
          <Button type="submit">Submit</Button>
        </div>
      </div>
    </form>
  );
};
